import { Component, OnInit } from '@angular/core';
import { AdministrationService } from '../administration.service';
import D3Wrapper from '../d3-wrapper';
import * as d3 from 'd3';
import * as dagreD3 from 'dagre-d3';
import { v4 as UUIDv4 } from 'uuid';
import { RESULT_STORE_ENPOINTS, TASK_MODEL_ACTIONS } from '../constants';

@Component({
  selector: 'tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss']
})
export class TasksComponent implements OnInit {

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private static readonly DEFAULT_PERIOD: string = "month";

  // user interactions frequency chart
  public userInteractionsFrequencyType: string = "fileImport";
  public userInteractionsFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isUserInteractionsFrequencyChartLoading: boolean = true;
  public isUserInteractionsFrequencyChartFailed: boolean = false;

  // type frequency chart
  public typeFrequencyType: string = "fileType";
  public typeFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isTypeFrequencyChartLoading: boolean = true;
  public isTypeFrequencyChartFailed: boolean = false;

  // images processed frequency chart
  public imagesProcessedFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isImagesProcessedFrequencyChartLoading: boolean = true;
  public isImagesProcessedFrequencyChartFailed: boolean = false;

  // avg time spent on tasks chart
  public avgTimeSpentOnTasksType: string = "fileImport";
  public avgTimeSpentOnTasksPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isAvgTimeSpentOnTasksChartLoading: boolean = true;
  public isAvgTimeSpentOnTasksChartFailed: boolean = false;

  // bounce rate chart
  public bounceRateType: string = "saveAndImageListed";
  public bounceRatePeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isBounceRateChartLoading: boolean = true;
  public isBounceRateChartFailed: boolean = false;

  // external search - avg time for fetch chart
  public avgTimeExternalSearchFetchPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isAvgTimeExternalSearchFetchChartLoading: boolean = true;
  public isAvgTimeExternalSearchFetchChartFailed: boolean = false;

  // external search - auto search query frequency chart
  public autoQueryExternalSearchFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isAutoQueryExternalSearchFrequencyChartLoading: boolean = true;
  public isAutoQueryExternalSearchFrequencyChartFailed: boolean = false;

  // external search - targeted apis frequency chart
  public targetedApisExternalSearchFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isTargetedApisExternalSearchFrequencyChartLoading: boolean = true;
  public isTargetedApisExternalSearchFrequencyChartFailed: boolean = false;

  // external search - manual vs external search frequency chart
  public manualVsExternalSearchFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isManualVsExternalSearchFrequencyChartLoading: boolean = true;
  public isManualVsExternalSearchFrequencyChartFailed: boolean = false;

  // external search - targeted licenses frequency chart
  public targetedLicensesExternalSearchFrequencyPeriod: string = TasksComponent.DEFAULT_PERIOD;
  public isTargetedLicensesExternalSearchFrequencyChartLoading: boolean = true;
  public isTargetedLicensesExternalSearchFrequencyChartFailed: boolean = false;

  // task model
  public taskModelSessionIds: [] = [];
  public taskModelSessionId: string = null;
  public isTaskModelLoading: boolean = true;
  public isTaskModelFailed: boolean = false;

  constructor(
    public adminService: AdministrationService,
  ) { }

  ngOnInit() {
    this.adminService.updateAuthentication().then((authenticated: boolean) => {
      if (authenticated) {
        this.updateUserInteractionsFrequencyChart();
        this.updateTypeFrequencyChart();
        this.updateImagesProcessedFrequencyChart();
        this.updateAvgTimeSpentOnTasksChart();
        this.updateBounceRateChart();
        this.updateAvgTimeExternalSearchFetchChart();
        this.updateAutoQueryExternalSearchFrequencyChart();
        this.updateTargetedApisExternalSearchFrequencyChart();
        this.updateManualVsExternalSearchFrequencyChart();
        this.updateTargetedLicensesExternalSearchFrequencyChart();
        this.getLastFiveDaysSessionIdsAndUpdateTaskModel();
      }
    });
  }

  private updateUserInteractionsFrequencyChart(): void {
    this.isUserInteractionsFrequencyChartLoading = true;
    this.isUserInteractionsFrequencyChartFailed = false;

    let resultStoreEnpoint = "";
    if (this.userInteractionsFrequencyType === "fileImport") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.FILE_IMPORT_FREQUENCY;
    } else if (this.userInteractionsFrequencyType === "externalSearch") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_FREQUENCY;
    } else if (this.userInteractionsFrequencyType === "manualReplacement") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.MANUAL_REPLACEMENT_FREQUENCY;
    } else if (this.userInteractionsFrequencyType === "publishAsOER") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.PUBLISH_AS_OER_FREQUENCY;
    } else if (this.userInteractionsFrequencyType === "excludeImage") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.EXCLUDE_IMAGE_FREQUENCY;
    } else if (this.userInteractionsFrequencyType === "saveClicked") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.SAVE_CLICKED_FREQUENCY;
    }

    this.adminService.getTimeChartData(resultStoreEnpoint, this.userInteractionsFrequencyPeriod)
      .then((response: any) => {
        // setup d3 bar chart
        D3Wrapper.setupBarChart("#user-interactions-freq-chart-svg", response);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupBarChart("#user-interactions-freq-chart-svg", response));
        // stop the loading bar
        this.isUserInteractionsFrequencyChartLoading = false;
      })
      .catch(reason => {
        this.isUserInteractionsFrequencyChartFailed = true;
        this.isUserInteractionsFrequencyChartLoading = false;
      });
  }

  private updateTypeFrequencyChart(): void {
    this.isTypeFrequencyChartLoading = true;
    this.isTypeFrequencyChartFailed = false;

    let resultStoreEnpoint = "";
    if (this.typeFrequencyType === "fileType") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.FILE_TYPE_FREQUENCY;
    } else if (this.typeFrequencyType === "imageType") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.IMAGE_TYPE_FREQUENCY;
    } else if (this.typeFrequencyType === "targetLicenseType") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.TARGET_LICENCE_TYPE_FREQUENCY;
    }

    this.adminService.getFrequencyRatioChartData(resultStoreEnpoint, this.typeFrequencyPeriod)
      .then((response: any) => {
        // setup d3 bar chart
        D3Wrapper.setupRatioBarChart("#type-freq-chart-svg", response, true);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#type-freq-chart-svg", response, true));
        // stop the loading bar
        this.isTypeFrequencyChartLoading = false;
      })
      .catch(reason => {
        this.isTypeFrequencyChartFailed = true;
        this.isTypeFrequencyChartLoading = false;
      });
  }

  private updateImagesProcessedFrequencyChart() {
    this.isImagesProcessedFrequencyChartLoading = true;
    this.isImagesProcessedFrequencyChartFailed = false;
    this.adminService.getTimeChartData(RESULT_STORE_ENPOINTS.TOTAL_IMAGES_FREQUENCY, this.imagesProcessedFrequencyPeriod)
      .then((totalImagesResponse: any) => {

        this.adminService.getTimeChartData(
          RESULT_STORE_ENPOINTS.PROCESSED_IMAGES_FREQUENCY, this.imagesProcessedFrequencyPeriod
        )
          .then((processedImagesResponse: any) => {

            // create data for d3 stacked bar chart
            const response = [];
            const subgroups = ["Total Images", "Processed Images"];
            for (let i = 0; i < totalImagesResponse.length; i++) {
              response.push({
                label: totalImagesResponse[i].label,
                [subgroups[0]]: totalImagesResponse[i].data,
                [subgroups[1]]: processedImagesResponse[i].data,
              });
            }
            // setup d3 stacked bar chart
            D3Wrapper.setupStackedBarChart("#images-processed-freq-chart-svg", subgroups, response);
            // setup the chart everytime the window resizes
            window.addEventListener("resize", () => D3Wrapper.setupStackedBarChart("#images-processed-freq-chart-svg", subgroups, response));
            // stop the loading bar
            this.isImagesProcessedFrequencyChartLoading = false;
          })
          .catch(reason => {
            console.log(reason);
            this.isImagesProcessedFrequencyChartFailed = true;
            this.isImagesProcessedFrequencyChartLoading = false;
          });
      })
      .catch(reason => {
        console.log(reason);
        this.isImagesProcessedFrequencyChartFailed = true;
        this.isImagesProcessedFrequencyChartLoading = false;
      });
  }

  private updateAvgTimeSpentOnTasksChart() {
    this.isAvgTimeSpentOnTasksChartLoading = true;
    this.isAvgTimeSpentOnTasksChartFailed = false;

    let resultStoreEnpoint = "";
    if (this.avgTimeSpentOnTasksType === "fileImport") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.FILE_IMPORT_AVG_TIME;
    } else if (this.avgTimeSpentOnTasksType === "externalSearch") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_AVG_TIME;
    } else if (this.avgTimeSpentOnTasksType === "manualReplacement") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.MANUAL_REPLACEMENT_AVG_TIME;
    } else if (this.avgTimeSpentOnTasksType === "overallSession") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.OVERALL_SESSION_AVG_TIME;
    }

    this.adminService.getTimeChartData(resultStoreEnpoint, this.avgTimeSpentOnTasksPeriod)
      .then((response: any) => {
        // setup d3 stacked bar chart
        D3Wrapper.setupLineChart("#avg-time-spent-on-tasks-chart-svg", response);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupLineChart("#avg-time-spent-on-tasks-chart-svg", response));
        // stop the loading bar
        this.isAvgTimeSpentOnTasksChartLoading = false;
      })
      .catch(reason => {
        this.isAvgTimeSpentOnTasksChartFailed = true;
        this.isAvgTimeSpentOnTasksChartLoading = false;
      });
  }

  private updateBounceRateChart(): void {
    this.isBounceRateChartLoading = true;
    this.isBounceRateChartFailed = false;

    let resultStoreEnpoint = "";
    if (this.bounceRateType === "saveAndImageListed") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.SAVE_AND_IMAGE_LISTED_FREQUENCY;
    } else if (this.bounceRateType === "saveAndStartClicked") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.SAVE_AND_START_CLICKED_FREQUENCY;
    } else if (this.bounceRateType === "startAndHomeLoaded") {
      resultStoreEnpoint = RESULT_STORE_ENPOINTS.START_AND_HOME_LOADED_FREQUENCY;
    }

    this.adminService.getFrequencyRatioChartData(resultStoreEnpoint, this.bounceRatePeriod)
      .then((response: any) => {
        // setup d3 bar chart
        D3Wrapper.setupRatioBarChart("#bounce-rate-chart-svg", response);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#bounce-rate-chart-svg", response));
        // stop the loading bar
        this.isBounceRateChartLoading = false;
      })
      .catch(reason => {
        this.isBounceRateChartFailed = true;
        this.isBounceRateChartLoading = false;
      });
  }

  private updateAvgTimeExternalSearchFetchChart() {
    this.isAvgTimeExternalSearchFetchChartLoading = true;
    this.isAvgTimeExternalSearchFetchChartFailed = false;
    this.adminService.getTimeChartData(
      RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_FETCH_AVG_TIME, this.avgTimeExternalSearchFetchPeriod
    )
      .then((response: any) => {
        // setup d3 stacked bar chart
        D3Wrapper.setupLineChart("#avg-time-external-search-fetch-chart-svg", response);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupLineChart("#avg-time-external-search-fetch-chart-svg", response));
        // stop the loading bar
        this.isAvgTimeExternalSearchFetchChartLoading = false;
      })
      .catch(reason => {
        console.log(reason);
        this.isAvgTimeExternalSearchFetchChartFailed = true;
        this.isAvgTimeExternalSearchFetchChartLoading = false;
      });
  }

  private updateAutoQueryExternalSearchFrequencyChart() {
    this.isAutoQueryExternalSearchFrequencyChartLoading = true;
    this.isAutoQueryExternalSearchFrequencyChartFailed = false;
    this.adminService.getTimeChartData(
      RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_AUTO_QUERY_CLICKED_FREQUENCY, this.autoQueryExternalSearchFrequencyPeriod
    )
      .then((autoSearchQueryClickedResponse: any) => {

        this.adminService.getTimeChartData(
          RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_AUTO_QUERY_OPTION_CLICKED_FREQUENCY, this.autoQueryExternalSearchFrequencyPeriod
        )
          .then((autoSearchQueryOptionClickedResponse: any) => {
            // create data for d3 stacked bar chart
            const response = [];
            const subgroups = ["Auto Search Query Clicked", "Auto Search Query Option Selected"];
            for (let i = 0; i < autoSearchQueryClickedResponse.length; i++) {
              response.push({
                label: autoSearchQueryClickedResponse[i].label,
                [subgroups[0]]: autoSearchQueryClickedResponse[i].data,
                [subgroups[1]]: autoSearchQueryOptionClickedResponse[i].data,
              });
            }
            // setup d3 stacked bar chart
            D3Wrapper.setupStackedBarChart("#auto-query-external-search-freq-chart-svg", subgroups, response);
            // setup the chart everytime the window resizes
            window.addEventListener("resize", () => D3Wrapper.setupStackedBarChart("#auto-query-external-search-freq-chart-svg", subgroups, response));
            // stop the loading bar
            this.isAutoQueryExternalSearchFrequencyChartLoading = false;
          })
          .catch(reason => {
            this.isAutoQueryExternalSearchFrequencyChartFailed = true;
            this.isAutoQueryExternalSearchFrequencyChartLoading = false;
          });
      })
      .catch(reason => {
        this.isAutoQueryExternalSearchFrequencyChartFailed = true;
        this.isAutoQueryExternalSearchFrequencyChartLoading = false;
      });
  }

  private updateTargetedApisExternalSearchFrequencyChart() {
    this.isTargetedApisExternalSearchFrequencyChartLoading = true;
    this.isTargetedApisExternalSearchFrequencyChartFailed = false;
    this.adminService.getFrequencyRatioChartData(
      RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_TARGET_APIS_FREQUENCY, this.targetedApisExternalSearchFrequencyPeriod
    )
      .then((response: any) => {
        // setup d3 bar chart
        D3Wrapper.setupRatioBarChart("#targeted-apis-external-search-freq-chart-svg", response);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#targeted-apis-external-search-freq-chart-svg", response));
        // stop the loading bar
        this.isTargetedApisExternalSearchFrequencyChartLoading = false;
      })
      .catch(reason => {
        this.isTargetedApisExternalSearchFrequencyChartFailed = true;
        this.isTargetedApisExternalSearchFrequencyChartLoading = false;
      });
  }

  private updateManualVsExternalSearchFrequencyChart() {
    this.isManualVsExternalSearchFrequencyChartLoading = true;
    this.isManualVsExternalSearchFrequencyChartFailed = false;
    this.adminService.getTimeChartData(
      RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_SEARCHED_FREQUENCY, this.manualVsExternalSearchFrequencyPeriod
    )
      .then((externalSearchResponse: any) => {

        this.adminService.getTimeChartData(
          RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_MANUALLY_REPLACED_FREQUENCY, this.manualVsExternalSearchFrequencyPeriod
        )
          .then((manualForExternalSearchedImageResponse: any) => {
            // create data for d3 stacked bar chart
            const response = [];
            const subgroups = ["External Search", "Manually Replaced"];
            for (let i = 0; i < externalSearchResponse.length; i++) {
              response.push({
                label: externalSearchResponse[i].label,
                [subgroups[0]]: externalSearchResponse[i].data,
                [subgroups[1]]: manualForExternalSearchedImageResponse[i].data,
              });
            }
            // setup d3 stacked bar chart
            D3Wrapper.setupStackedBarChart("#manual-vs-external-search-freq-chart-svg", subgroups, response);
            // setup the chart everytime the window resizes
            window.addEventListener("resize", () => D3Wrapper.setupStackedBarChart("#manual-vs-external-search-freq-chart-svg", subgroups, response));
            // stop the loading bar
            this.isManualVsExternalSearchFrequencyChartLoading = false;
          })
          .catch(reason => {
            this.isManualVsExternalSearchFrequencyChartFailed = true;
            this.isManualVsExternalSearchFrequencyChartLoading = false;
          });
      })
      .catch(reason => {
        this.isManualVsExternalSearchFrequencyChartFailed = true;
        this.isAutoQueryExternalSearchFrequencyChartLoading = false;
      });
  }

  private updateTargetedLicensesExternalSearchFrequencyChart() {
    this.isTargetedLicensesExternalSearchFrequencyChartLoading = true;
    this.isTargetedLicensesExternalSearchFrequencyChartFailed = false;
    this.adminService.getFrequencyRatioChartData(
      RESULT_STORE_ENPOINTS.EXTERNAL_SEARCH_TARGET_LICENSES_FREQUENCY, this.targetedLicensesExternalSearchFrequencyPeriod
    )
      .then((response: any) => {
        // setup d3 bar chart
        D3Wrapper.setupRatioBarChart("#targeted-licenses-external-search-freq-chart-svg", response, true);
        // setup the chart everytime the window resizes
        window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#targeted-licenses-external-search-freq-chart-svg", response, true));
        // stop the loading bar
        this.isTargetedLicensesExternalSearchFrequencyChartLoading = false;
      })
      .catch(reason => {
        this.isTargetedLicensesExternalSearchFrequencyChartFailed = true;
        this.isTargetedLicensesExternalSearchFrequencyChartLoading = false;
      });
  }

  private getLastFiveDaysSessionIdsAndUpdateTaskModel() {
    this.isTaskModelLoading = true;
    this.isTaskModelFailed = false;
    this.adminService.getLastFiveDaysSessionIds()
      .then((response: any) => {
        this.taskModelSessionIds = response;
        if (response.length > 0) {
          this.taskModelSessionId = response[0];
        }
        this.updateTaskModel();
      })
      .catch(reason => {
        this.isTaskModelFailed = true;
        this.isTaskModelLoading = false;
      });
  }

  private updateTaskModel() {
    this.isTaskModelLoading = true;
    this.isTaskModelFailed = false;

    if (this.taskModelSessionId) {
      this.adminService.getTaskModelForSessionId(this.taskModelSessionId)
        .then((response: any) => {
          // create a new directed graph
          const g = new dagreD3.graphlib.Graph().setGraph({ compound: false, multigraph: true });
          let taskIndex = 0;
          let previousNonImageTask = null;
          let previousImageTask = null;

          for (taskIndex = 0; taskIndex < response.tasks.length; taskIndex++) {
            if (!Array.isArray(response.tasks[taskIndex])) {
              const currentNonImageTask = response.tasks[taskIndex];
              if (currentNonImageTask.action === TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_STARTED) {
                // just add a circular node without any edges
                g.setNode(
                  currentNonImageTask.action, {
                  label: "",
                  shape: "circle",
                  class: ["start-flow"],
                  hovertext: ''
                }
                );
              } else if (currentNonImageTask.action === TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED) {
                // add a circular node
                g.setNode(
                  currentNonImageTask.action, {
                  label: "",
                  shape: "circle",
                  class: ["end-flow"],
                  hovertext: ''
                }
                );

                // add an edge to the previous non image task
                g.setEdge(
                  (previousImageTask === null) ? previousNonImageTask.action : previousImageTask.action + "_" + (taskIndex - 1),
                  currentNonImageTask.action,
                  {
                    style: "stroke: #4472C4; stroke-width: 1.5 px; fill: #FFF;",
                    arrowheadStyle: "stroke: #4472C4; fill: #4472C4l; fill-opacity: 1.0;",
                    curve: d3.curveBasis
                  }
                );
              } else if (currentNonImageTask.action === TASK_MODEL_ACTIONS.FIRST_STEPS_DIALOG_STARTED) {
                // skip the task
                continue;
              } else {
                // cases where we need to add a rectangle node and an edge to previous task
                if (currentNonImageTask.action === TASK_MODEL_ACTIONS.FIRST_STEPS_DIALOG_FINISHED) {
                  // rename it to image_listed
                  currentNonImageTask.action = TASK_MODEL_ACTIONS.IMAGE_LISTED;
                }

                // add a rectange node
                g.setNode(
                  currentNonImageTask.action, {
                  label: currentNonImageTask.action,
                  shape: "rect",
                  class: ["task-flow"],
                  hovertext: ''
                }
                );

                // add an edge if previous task exists
                if (previousNonImageTask) {
                  g.setEdge(
                    previousNonImageTask.action,
                    currentNonImageTask.action,
                    {
                      style: "stroke: #4472C4; stroke-width: 1.5 px; fill: #FFF;",
                      arrowheadStyle: "stroke: #4472C4; fill: #4472C4l; fill-opacity: 1.0;",
                      curve: d3.curveBasis
                    }
                  );
                }
              }
              previousNonImageTask = currentNonImageTask;
            } else {
              let noOfAutoQueries = 0;
              let noOfManualQueries = 0;
              // array of tasks for source image
              for (let j = 0; j < response.tasks[taskIndex].length; j++) {
                const currentImageTask = response.tasks[taskIndex][j];
                // skip if its externalImage_fetched and external image searches for pages other than 1
                if (
                  currentImageTask.action === TASK_MODEL_ACTIONS.EXTERNAL_IMAGE_FETCHED ||
                  (currentImageTask.action === TASK_MODEL_ACTIONS.EXTERNAL_IMAGE_SEARCHED && currentImageTask.data.currentPage > 1)
                ) {
                  // skip "externalImage_fetched" task since we don't show it in the model
                  continue;
                } else if (currentImageTask.action === TASK_MODEL_ACTIONS.IMAGE_REPLACED) {
                  // we dont want to reuse the same "image_replaced" node if the image was replaced multiple times
                  const newActionName = currentImageTask.action + "_" + UUIDv4();

                  g.setNode(
                    newActionName + "_" + taskIndex, {
                    label: currentImageTask.action,
                    shape: "rect",
                    class: ["task-flow"],
                    hovertext: ''
                  }
                  );

                  // rename the action with the new name
                  currentImageTask.action = newActionName;

                  g.setEdge(
                    previousImageTask.action + "_" + taskIndex,
                    currentImageTask.action + "_" + taskIndex,
                    {
                      label: previousImageTask.data.replacementAction,
                      arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                      curve: d3.curveBasis
                    }
                  );
                } else if (currentImageTask.action === TASK_MODEL_ACTIONS.REPLACED_IMAGE_UNDID) {
                  // add a new negative rect node and a negative edge from the previous task (image_replaced)
                  g.setNode(
                    currentImageTask.action + "_" + taskIndex, {
                    label: currentImageTask.action,
                    shape: "rect",
                    class: ["negative-flow"],
                    hovertext: ''
                  }
                  );

                  g.setEdge(
                    previousImageTask.action + "_" + taskIndex,
                    currentImageTask.action + "_" + taskIndex,
                    {
                      style: "stroke: #FF0000; stroke-width: 1.5 px; fill: #FFF;",
                      arrowheadStyle: "stroke: #FF0000; fill: #FF0000; fill-opacity: 1.0;",
                      curve: d3.curveBasis
                    }
                  );
                } else if (currentImageTask.action === TASK_MODEL_ACTIONS.IMAGE_SELECTED && j !== 0) {
                  // reuse the same "image_selected" node, but add a negative flow edge from the previous task
                  let label = "";
                  if ("data" in previousImageTask) {
                    if ("replacementAction" in previousImageTask.data) {
                      label = previousImageTask.data.replacementAction;
                    }
                  }

                  g.setEdge(
                    previousImageTask.action + "_" + taskIndex,
                    currentImageTask.action + "_" + taskIndex,
                    {
                      label: label,
                      style: "stroke: #FF0000; stroke-width: 1.5 px; fill: #FFF;",
                      arrowheadStyle: "stroke: #FF0000; fill: #FF0000; fill-opacity: 1.0;",
                      curve: d3.curveBasis
                    }
                  );
                } else if (currentImageTask.action === TASK_MODEL_ACTIONS.IMAGE_EDITED) {
                  let label = "(C";
                  if (currentImageTask.data.isRotated) {
                    label += ", R)";
                  } else {
                    label += ")";
                  }

                  g.setNode(
                    currentImageTask.action + "_" + taskIndex, {
                    label: currentImageTask.action + " " + label,
                    shape: "rect",
                    class: ["task-flow"],
                    hovertext: ''
                  }
                  );

                  g.setEdge(
                    previousImageTask.action + "_" + taskIndex,
                    currentImageTask.action + "_" + taskIndex,
                    {
                      arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                      curve: d3.curveBasis
                    }
                  );
                } else {

                  g.setNode(
                    currentImageTask.action + "_" + taskIndex, {
                    label: currentImageTask.action,
                    shape: "rect",
                    class: ["task-flow"],
                    hovertext: ''
                  }
                  );

                  // if its an externalImage_searched task, add two new nodes for 'auto_query' and 'manual_query'
                  if (currentImageTask.action === TASK_MODEL_ACTIONS.EXTERNAL_IMAGE_SEARCHED) {

                    const isAutoSearchQueryClicked = currentImageTask.data.isAutoSearchQueryClicked;
                    const isAutoSearchQueryOptionSelected = currentImageTask.data.isAutoSearchQueryOptionSelected;

                    // by default create optional nodes for auto query and manual query
                    if (!g.nodes().includes('auto_query' + "_" + taskIndex)) {
                      g.setNode(
                        'auto_query' + "_" + taskIndex, {
                        label: 'auto_query',
                        shape: "rect",
                        class: ["optional-flow"],
                        hovertext: ''
                      }
                      );
                    }

                    if (!g.nodes().includes('manual_query' + "_" + taskIndex)) {
                      g.setNode(
                        'manual_query' + "_" + taskIndex, {
                        label: 'manual_query',
                        shape: "rect",
                        class: ["optional-flow"],
                        hovertext: ''
                      }
                      );
                    }

                    if (isAutoSearchQueryClicked && isAutoSearchQueryOptionSelected) {
                      noOfAutoQueries = noOfAutoQueries + 1;
                      g.setNode(
                        'auto_query' + "_" + taskIndex, {
                        label: 'auto_query (' + noOfAutoQueries + ')',
                        shape: "rect",
                        class: ["task-flow"],
                        hovertext: ''
                      }
                      );

                      g.setEdge(
                        currentImageTask.action + "_" + taskIndex,
                        'auto_query' + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                      g.setEdge(
                        'auto_query' + "_" + taskIndex,
                        currentImageTask.action + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                      if (noOfManualQueries === 0) {
                        g.setEdge(
                          currentImageTask.action + "_" + taskIndex,
                          'manual_query' + "_" + taskIndex,
                          {
                            style: "stroke: #595959; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                            arrowheadStyle: "stroke: #595959; fill: #595959; fill-opacity: 1.0;",
                            arrowhead: 'undirected',
                            curve: d3.curveBasis
                          }
                        );
                      }

                    } else if (isAutoSearchQueryClicked) {
                      noOfAutoQueries = noOfAutoQueries + 1;
                      g.setNode(
                        'auto_query' + "_" + taskIndex, {
                        label: 'auto_query (' + noOfAutoQueries + ')',
                        shape: "rect",
                        class: ["task-flow"],
                        hovertext: ''
                      }
                      );

                      noOfManualQueries = noOfManualQueries + 1;
                      g.setNode(
                        'manual_query' + "_" + taskIndex, {
                        label: 'manual_query (' + noOfManualQueries + ')',
                        shape: "rect",
                        class: ["negative-flow"],
                        hovertext: ''
                      }
                      );

                      g.setEdge(
                        currentImageTask.action + "_" + taskIndex,
                        'auto_query' + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                      g.setEdge(
                        'auto_query' + "_" + taskIndex,
                        'manual_query' + "_" + taskIndex,
                        {
                          style: "stroke: #FF0000; stroke-width: 1.5 px; fill: #FFF;",
                          arrowheadStyle: "stroke: #FF0000; fill: #FF0000; fill-opacity: 1.0;",
                          curve: d3.curveBasis
                        }
                      );

                      g.setEdge(
                        'manual_query' + "_" + taskIndex,
                        currentImageTask.action + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                    } else {
                      noOfManualQueries = noOfManualQueries + 1;
                      g.setNode(
                        'manual_query' + "_" + taskIndex, {
                        label: 'manual_query (' + noOfManualQueries + ')',
                        shape: "rect",
                        class: ["task-flow"],
                        hovertext: ''
                      }
                      );

                      g.setEdge(
                        currentImageTask.action + "_" + taskIndex,
                        'manual_query' + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                      g.setEdge(
                        'manual_query' + "_" + taskIndex,
                        currentImageTask.action + "_" + taskIndex,
                        {
                          curve: d3.curveBasis,
                          arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        }
                      );

                      if (noOfAutoQueries === 0) {
                        g.setEdge(
                          currentImageTask.action + "_" + taskIndex,
                          'auto_query' + "_" + taskIndex,
                          {
                            style: "stroke: #595959; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                            arrowheadStyle: "stroke: #595959; fill: #595959; fill-opacity: 1.0;",
                            arrowhead: 'undirected',
                            curve: d3.curveBasis
                          }
                        );
                      }
                    }
                  }

                  // Add edges
                  // if its the first image task, add an edge to the previous non image task (image_listed)
                  if (j === 0) {
                    g.setEdge(
                      previousNonImageTask.action,
                      response.tasks[taskIndex][0].action + "_" + taskIndex,
                      {
                        arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        curve: d3.curveBasis
                      }
                    );
                  } else {
                    // if its from the second tasks, add an edge
                    let label = "";
                    if ("data" in previousImageTask) {
                      if ("replacementAction" in previousImageTask.data) {
                        label = previousImageTask.data.replacementAction;
                      }
                    }

                    g.setEdge(
                      previousImageTask.action + "_" + taskIndex,
                      currentImageTask.action + "_" + taskIndex,
                      {
                        label: label,
                        arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                        curve: d3.curveBasis
                      }
                    );
                  }
                }
                previousImageTask = currentImageTask;
              }
            }
          }

          // Add the missing ideal task flows based on previous taskIndex:
          // 1  -> TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_STARTED
          // 2  -> TASK_MODEL_ACTIONS.FIRST_STEPS_DIALOG_STARTED
          // 3  -> TASK_MODEL_ACTIONS.FILE_IMPORTED
          // 4  -> TASK_MODEL_ACTIONS.FIRST_STEPS_DIALOG_FINISHED // TASK_MODEL_ACTIONS.IMAGE_LISTED
          // last -> TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED
          if (taskIndex === 1 || taskIndex === 2) {
            g.setNode(
              TASK_MODEL_ACTIONS.FILE_IMPORTED, {
              label: TASK_MODEL_ACTIONS.FILE_IMPORTED,
              shape: "rect",
              class: ["missing-flow"],
              hovertext: ''
            }
            );
            g.setNode(
              TASK_MODEL_ACTIONS.IMAGE_LISTED, {
              label: TASK_MODEL_ACTIONS.IMAGE_LISTED,
              shape: "rect",
              class: ["missing-flow"],
              hovertext: ''
            }
            );
            g.setNode(
              TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED, {
              label: "",
              shape: "circle",
              class: ["missing-flow"],
              hovertext: ''
            }
            );

            g.setEdge(
              TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_STARTED, TASK_MODEL_ACTIONS.FILE_IMPORTED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
            g.setEdge(
              TASK_MODEL_ACTIONS.FILE_IMPORTED, TASK_MODEL_ACTIONS.IMAGE_LISTED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
            g.setEdge(
              TASK_MODEL_ACTIONS.FILE_IMPORTED, TASK_MODEL_ACTIONS.IMAGE_LISTED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
            g.setEdge(
              TASK_MODEL_ACTIONS.IMAGE_LISTED, TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
          } else if (taskIndex === 3) {
            g.setNode(
              TASK_MODEL_ACTIONS.IMAGE_LISTED, {
              label: TASK_MODEL_ACTIONS.IMAGE_LISTED,
              shape: "rect",
              class: ["missing-flow"],
              hovertext: ''
            }
            );
            g.setNode(
              TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED, {
              label: "",
              shape: "circle",
              class: ["missing-flow"],
              hovertext: ''
            }
            );

            g.setEdge(
              TASK_MODEL_ACTIONS.FILE_IMPORTED, TASK_MODEL_ACTIONS.IMAGE_LISTED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
            g.setEdge(
              TASK_MODEL_ACTIONS.FILE_IMPORTED, TASK_MODEL_ACTIONS.IMAGE_LISTED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );
            g.setEdge(
              TASK_MODEL_ACTIONS.IMAGE_LISTED, TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4; fill-opacity: 1.0;",
                curve: d3.curveBasis
              }
            );

          } else if (
            !Array.isArray(response.tasks[taskIndex - 1]) &&
            response.tasks[taskIndex - 1].action !== TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED) {
            g.setNode(
              TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED, {
              label: "",
              shape: "circle",
              class: ["missing-flow"],
              hovertext: ''
            }
            );

            g.setEdge(
              TASK_MODEL_ACTIONS.IMAGE_LISTED, TASK_MODEL_ACTIONS.OER_CONVERSION_PROCESS_FINISHED,
              {
                style: "stroke: #4472C4; stroke-width: 1.5 px; stroke-dasharray: 5, 5; fill: #FFF;",
                arrowheadStyle: "stroke: #4472C4; fill: #4472C4",
                curve: d3.curveBasis
              }
            );
          }

          const svg = d3.select("#task-model-svg");
          const inner = svg.select("g");

          // Set the rankdir
          g.graph().rankdir = 'LR'; // 'TB' (aka vertical) or 'LR' (aka horizontal)
          g.graph().nodesep = 100;

          // Set up zoom support
          const zoom = d3.zoom().on("zoom", function (event) {
            inner.attr("transform", event.transform);
          });
          svg.call(zoom);

          // Run the renderer. This is what draws the final graph.
          const render = new dagreD3.render();
          render(inner, g);

          // Center the dag
          // Get Dagre Graph dimensions
          const graphWidth = g.graph().width;
          const graphHeight = g.graph().height;
          // Get SVG dimensions
          const svgWidth = parseInt(svg.style("width").replace(/px/, ""), 10);
          const svgHeight = parseInt(svg.style("height").replace(/px/, ""), 10);
          // Calculate applicable scale for zoom
          const zoomScale = Math.min(Math.min(svgWidth / graphWidth, svgHeight / graphHeight), 1.0);
          // move center of the graph to center of svg
          zoom.translateTo(svg as any, graphWidth / 2, graphHeight / 2, [svgWidth / 2, svgHeight / 2]);
          zoom.scaleTo(svg as any, zoomScale);

          d3.select("#svg-zoom-in").on("click", function () {
            zoom.scaleBy((svg as any).transition().duration(750), 1.2);
          });
          d3.select("#svg-zoom-out").on("click", function () {
            zoom.scaleBy((svg as any).transition().duration(750), 0.8);
          });

          // TODO: Allow dragging of nodes

          this.isTaskModelLoading = false;
        })
        .catch(reason => {
          console.log(reason);
          this.isTaskModelFailed = true;
          this.isTaskModelLoading = false;
        });
    } else {
      const svg = d3.select("#task-model-svg");
      const inner = svg.select("g");

      inner.append("text")
        .text("No session ids found")
        .attr("x", "45%")
        .attr("y", "50%");

    }

    this.isTaskModelLoading = false;
  }
}
